import React, { ReactNode, useState } from 'react';

import '../Home.scss';
import { Button, ModalHeader, ModalBody, ModalFooter, Dropdown, Surface, Col, Row, Typography, MessageBlock, MessageBlockContent, TextField } from 'cfa-react-components';
import { ICapture, ICaptureConfig } from 'interfaces';
import { CaptureConfigService, CaptureService, parseAxiosError } from 'services';

interface ICaptureModalProps {
    captureToRepeat: ICapture,
    onHide: () => void,
    refresh: () => void,
}

export function CaptureModal({ captureToRepeat, onHide, refresh }: ICaptureModalProps) {

    const maxCaptureDuration = 3600;

    // if we're repeating a capture, set defaults
    const [config, setConfig] = useState<ICaptureConfig | null>(
        captureToRepeat ? CaptureConfigService.allCaptureConfigsMap[captureToRepeat.configUUID] : null);
    const [captureName, setCaptureName] = useState<string>(
        captureToRepeat ? captureToRepeat.name + " - Copy" : "");

    const [loading, setLoading] = useState(false);
    const [warning, setWarning] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const setAndValidateConfig = (cfg: ICaptureConfig) => {
        setConfig(cfg);
        if (cfg.durationMinutes > maxCaptureDuration) setWarning("Capture duration exceeds " + maxCaptureDuration + " minutes");
        else setWarning(null);
    }

    const submitCapture = () => {
        if (!config || captureName == "") return;

        setLoading(true);
        setErrorMessage(null);

        CaptureService.postCapture(captureName, config)
            .then(() => {
                refresh();
                onHide();
            })
            .catch((e) => {
                setErrorMessage(parseAxiosError(e));
                setLoading(false);
            })
    }

    const surfaceContent: ReactNode = config && (
        <div style={{width: '100%', textAlign: 'left', fontSize: '14px', lineHeight: '18px', paddingLeft: 15}}>
            <Row>
                <Col xs={2}>{'Type:'}</Col>
                <Col xs={10}>{config.sourceType}</Col>
            </Row>
            <Row className='noTopMargin'>
                <Col xs={2}>{'Source:'}</Col>
                <Col xs={10}>{config.sourceParams.location}</Col>
            </Row>
            <Row className='noTopMargin'>
                <Col xs={2}>{'Secret:'}</Col>
                <Col xs={10}>{config.sourceParams.secretPath}</Col>
            </Row>
            <Row className='noTopMargin'>
                <Col xs={2}>{'Duration:'}</Col>
                <Col xs={10}>{config.durationMinutes} {config.durationMinutes > 1 ? 'minutes' : 'minute'}</Col>
            </Row>
            <Row className='noTopMargin'>
                <Col xs={2}>{'Delay:'}</Col>
                <Col xs={10}>{config.delayMinutes} {config.delayMinutes > 1 ? 'minutes' : 'minute'}</Col>
            </Row>
            <Row className='noTopMargin rowLastItem'>
                <Col xs={2}>{'TTL:'}</Col>
                <Col xs={10}>{config.TTL} {config.TTL > 1 ? 'days' : 'day'}</Col>
            </Row>
        </div>
    );

    return (<>

        <ModalHeader>
            {captureToRepeat ? "Repeat Capture" : "Start Capture"}
        </ModalHeader>

        <ModalBody>
            <Row>
                <Typography>
                    Directions: Choose a capture configuration from the options below, 
                    then start the capture. Note that the captures may take a long time, 
                    so be careful not to start too many at once.
                </Typography>
            </Row>
            {warning && (<Row style={{justifyContent: 'center'}}>
                <MessageBlock severity='warning'>
                    <MessageBlockContent>{warning.toString()}</MessageBlockContent>
                </MessageBlock>
            </Row>)}
            {errorMessage && (<Row style={{justifyContent: 'center'}}>
                <MessageBlock severity='error'>
                    <MessageBlockContent>{errorMessage.toString()}</MessageBlockContent>
                </MessageBlock>
            </Row>)}
            <Row>
                <Col>
                    <TextField
                        style={{width: '100%', paddingBottom: '10px'}}
                        placeholder='Enter Capture Name'
                        value={captureName}
                        onChange={(e) => setCaptureName(e.target.value)}
                    />
                    <Dropdown
                        style={{width: '100%'}}
                        placeholder='Select Capture Config'
                        value={config}
                        onChange={(newVal) => setAndValidateConfig(newVal)}
                        options={CaptureConfigService.allCaptureConfigs}
                        renderOption={(c) => <>{c.name}</>}
                        // must have these options, since we're passing in an array of objects
                        getOptionText={(c) => c.name}
                        getOptionId={(c) => c.captureConfigUUID}
                        disabled={captureToRepeat != null}
                    />
                </Col>
                <Col>
                    <Surface
                        as="div"
                        elevation={1}
                        radii="md"
                        style={{
                            justifyContent: 'center',
                            display: 'flex',
                            height: '100%',
                            width: '100%'
                        }}
                        variant="default"
                    >
                        {surfaceContent}
                    </Surface>
                </Col>
            </Row>
        </ModalBody>

        <ModalFooter>
            <Button variant="outlined" onClick={onHide}>
                Close
            </Button>
            <Button 
                variant="filled" 
                onClick={submitCapture}
                disabled={!config || captureName == ""}
                loading={loading}
            >
                {loading ? 'Submitting...' : 'Submit'}
            </Button>
        </ModalFooter>

    </>);
}